import React, { useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Sparkles, Eye, Compass } from "lucide-react";
import { ORACLES } from "@/constants/testIds";
import { askOracle } from "@/lib/api";
import { useAccount } from "@/lib/account";

const ORACLES_META = [
  {
    id: "pendule_tigre",
    label: "Pendule Œil de Tigre",
    tagline: "Oui ou non — un miroir ludique, jamais une décision à ta place.",
    Icon: Eye,
    accent: "#d4884a",
    animation: "swing",
    dailyLimit: 10,
    yesNo: true,
  },
  {
    id: "universel",
    label: "Oracle Universel",
    tagline: "Dix voix celtiques tirées au sort — Barde, Druide, Vate, Farfadet, Korrigan, Gaïa.",
    Icon: Compass,
    accent: "#e6d9a2",
    animation: "sparkle",
    dailyLimit: 10,
  },
];

export default function Oracles() {
  const { refresh } = useAccount();

  return (
    <section id="oracles" data-testid={ORACLES.section} className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-10">
          <div className="overline mb-4">
            <span className="kicker-line" />
            Deux oracles · traditions gréco-celtiques · gratuits
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            La sagesse <span className="italic" style={{ color: "var(--gold-glow)" }}>vous appartient</span>.
          </h2>
          <p className="mt-4" style={{ color: "var(--violet-soft)" }}>
            Le Pendule Œil de Tigre tranche par oui ou non, l'Oracle Universel fait parler
            les voix celtiques. Aucun ne prédit : tous deux vous ramènent à vous.
          </p>
        </div>

        <div
          data-testid={ORACLES.counter}
          className="mystic-card rounded-2xl p-4 mb-8 inline-flex items-center gap-3"
        >
          <Sparkles size={16} className="text-gold" />
          <div className="text-sm" style={{ color: "var(--violet-soft)" }}>
            Gratuit et illimité — mais souviens-toi : la réponse qui compte est la tienne.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ORACLES_META.map((meta, i) => (
            <OracleCard key={meta.id} meta={meta} onAnswer={refresh} />
          ))}
        </div>
      </div>
    </section>
  );
}

function OracleCard({ meta, onAnswer }) {
  const { email } = useAccount();
  const [q, setQ] = useState("");
  const [ans, setAns] = useState(null);
  const [loading, setLoading] = useState(false);

  const ask = async (e) => {
    e.preventDefault();
    if (!q.trim()) return toast.error("Formulez une question.");
    setLoading(true);
    setAns(null);
    try {
      await new Promise((r) => setTimeout(r, 900));
      const data = await askOracle(meta.id, q.trim(), email);
      setAns(data);
      onAnswer?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("L'oracle se retire un instant. Réessayez.");
      }
    } finally {
      setLoading(false);
    }
  };

  const Icon = meta.Icon;

  return (
    <article
      data-testid={`oracle-card-${meta.id}`}
      className="mystic-card gold-border rounded-3xl p-6 flex flex-col"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: `${meta.accent}22`,
            border: `1px solid ${meta.accent}66`,
          }}
        >
          <Icon size={18} style={{ color: meta.accent }} className={meta.animation} />
        </div>
        <div className="flex-1">
          <div className="font-display text-sm uppercase tracking-widest" style={{ color: meta.accent }}>
            {meta.label}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--violet-soft)" }}>
            {meta.tagline}
          </div>
        </div>
      </div>

      <form onSubmit={ask} className="space-y-3 mt-auto">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Votre question…"
          data-testid={`oracle-input-${meta.id}`}
          className="mystic-input text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`oracle-ask-${meta.id}`}
          className="btn-ghost w-full !text-[11px] !py-2.5 disabled:opacity-60"
        >
          {loading ? "…" : "Interroger l'oracle"}
        </button>
      </form>

      {ans && (
        <div
          data-testid={`oracle-answer-${meta.id}`}
          className="mt-4 p-4 rounded-2xl fade-up"
          style={{
            background: `${meta.accent}0F`,
            border: `1px solid ${meta.accent}33`,
          }}
        >
          <p className="font-serif italic text-base leading-relaxed" style={{ color: "var(--star)" }}>
            <ReactMarkdown components={{ p: ({ children }) => <span>« {children} »</span> }}>{ans.message}</ReactMarkdown>
          </p>
          <ChatDisclaimer testId={`oracle-ai-disclaimer-${meta.id}`} />
        </div>
      )}
    </article>
  );
}
